/**
 * Skeleton Loading Components
 *
 * Creates loading placeholder elements for blocks while they stream.
 */

import { formatBlockType } from './block-parser';
import { createActivityLoader } from '../loading/activity-loader';

// =============================================================================
// BLOCK SKELETON
// =============================================================================

/**
 * Create a skeleton loading placeholder for a block.
 */
export function createBlockSkeleton(blockType: string): HTMLElement {
  const skeleton = document.createElement('div');
  skeleton.className = 'pms-block-skeleton';

  // Different skeletons based on block type
  if (blockType === 'detail-page' || blockType === 'browse' || blockType === 'cards') {
    // Large block skeleton with hero + grid
    skeleton.innerHTML = `
      <div class="pms-skeleton-hero">
        <div class="pms-skeleton-shimmer"></div>
      </div>
      <div class="pms-skeleton-grid">
        <div class="pms-skeleton-item"><div class="pms-skeleton-shimmer"></div></div>
        <div class="pms-skeleton-item"><div class="pms-skeleton-shimmer"></div></div>
        <div class="pms-skeleton-item"><div class="pms-skeleton-shimmer"></div></div>
      </div>
    `;
  } else if (blockType === 'stats') {
    // Stats skeleton
    skeleton.innerHTML = `
      <div class="pms-skeleton-stats">
        <div class="pms-skeleton-stat"><div class="pms-skeleton-shimmer"></div></div>
        <div class="pms-skeleton-stat"><div class="pms-skeleton-shimmer"></div></div>
        <div class="pms-skeleton-stat"><div class="pms-skeleton-shimmer"></div></div>
        <div class="pms-skeleton-stat"><div class="pms-skeleton-shimmer"></div></div>
      </div>
    `;
  } else {
    // Generic skeleton
    skeleton.innerHTML = `
      <div class="pms-skeleton-line pms-skeleton-line-lg"><div class="pms-skeleton-shimmer"></div></div>
      <div class="pms-skeleton-line"><div class="pms-skeleton-shimmer"></div></div>
      <div class="pms-skeleton-line pms-skeleton-line-sm"><div class="pms-skeleton-shimmer"></div></div>
    `;
  }

  return skeleton;
}

// =============================================================================
// BLOCK PLACEHOLDER (with loading text)
// =============================================================================

/**
 * Create a loading placeholder for an in-progress block.
 */
export function createBlockPlaceholder(blockType: string): HTMLElement {
  const placeholder = document.createElement('div');
  placeholder.className = 'pms-block-placeholder pms-block-loading';
  placeholder.innerHTML = `
    <div class="pms-block-placeholder-shimmer"></div>
    <div class="pms-block-placeholder-content">
      <div class="pms-block-placeholder-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M3 9h18M9 21V9"/>
        </svg>
      </div>
      <span class="pms-block-placeholder-text">Loading ${formatBlockType(blockType)}...</span>
    </div>
  `;

  return placeholder;
}

// =============================================================================
// LOADING INDICATOR
// =============================================================================

/**
 * Create the streaming loading indicator.
 * Uses the unified activity loader (ping-pong animation + rotating messages).
 */
export function createLoadingIndicator(): HTMLElement {
  return createActivityLoader();
}

// =============================================================================
// ERROR BLOCK
// =============================================================================

/**
 * Create an error block for failed block rendering.
 */
export function createBlockError(blockType: string): HTMLElement {
  const errorEl = document.createElement('div');
  errorEl.className = 'pms-block pms-block-error';
  errorEl.innerHTML = `
    <div class="pms-block-error-icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </div>
    <div class="pms-block-error-text">Failed to render ${blockType} block</div>
  `;

  return errorEl;
}
